import type { ReactNode, ButtonHTMLAttributes } from "react";
import { CATEGORIES , verificationLabel } from "@oshimap/config";
import { categoryColors, CATEGORY_ICONS, EVENT_ICON, ICON_VIEWBOX } from "@oshimap/design-tokens";
import { normalizeCategoryCode, type VerificationStatus } from "@oshimap/domain";
import { formatDay, hostLabel, safeHref } from "@oshimap/utils";
import "./ui.css";

type Variant = "primary" | "secondary" | "ghost" | "oshi";
type Size = "sm" | "md" | "lg";

export function Button({
  variant = "primary", size = "md", block, children, ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; block?: boolean }) {
  return (
    <button {...rest} className={`om-btn om-btn--${variant} om-btn--${size}${block ? " om-btn--block" : ""}${rest.className ? ` ${rest.className}` : ""}`}>
      {children}
    </button>
  );
}

export function Chip({ pressed, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { pressed?: boolean }) {
  return <button {...rest} aria-pressed={pressed} className="om-chip">{children}</button>;
}

/**
 * 카테고리 아이콘.
 *
 * 모양은 `@oshimap/design-tokens` 한 곳에서 옵니다 — Native 가 같은 좌표를
 * 읽습니다. 전에는 이모지(🎪 ☕ 🛍️)였고, 기기마다 다르게 그려졌습니다.
 *
 * 탭 아이콘과 달리 **면으로만** 그립니다. 이 아이콘은 지도 핀 안에서 20px
 * 아래까지 줄어드는데, 거기서는 선이 1px 밑으로 눌려 속이 메워집니다.
 */
export function CategoryIcon({ code, size = 20 }: { code: string; size?: number }) {
  const parts = CATEGORY_ICONS[normalizeCategoryCode(code)] ?? CATEGORY_ICONS["DEFAULT"];
  return (
    <svg viewBox={ICON_VIEWBOX} width={size} height={size} aria-hidden="true" fill="currentColor" focusable="false">
      {parts?.map((part) => <path key={part.d} d={part.d} fillRule={part.evenOdd ? "evenodd" : undefined} />)}
    </svg>
  );
}

/** 카테고리가 없는 행사 자리. 장소가 아니라 "때가 있는 것"이라 티켓을 씁니다. */
export function EventIcon({ size = 20 }: { size?: number }) {
  return (
    <svg viewBox={ICON_VIEWBOX} width={size} height={size} aria-hidden="true" fill="currentColor" focusable="false">
      {EVENT_ICON.map((part) => <path key={part.d} d={part.d} fillRule={part.evenOdd ? "evenodd" : undefined} />)}
    </svg>
  );
}

/** CAT-1: 색 단독으로 카테고리를 전달하지 않는다. 아이콘 + 라벨 + 색을 항상 함께 쓴다. */
export function CategoryChip({ code }: { code: string }) {
  const c = normalizeCategoryCode(code);
  const def = CATEGORIES[c];
  const color = categoryColors[c];
  return (
    <span className="om-cat" style={{ background: color.bg, color: color.fg }}>
      <CategoryIcon code={c} size={16} />
      {def.label}
    </span>
  );
}

/* 이름은 공유 값에서 가져온다 (Native 와 같은 말을 쓰기 위해). 색 구분만 화면 고유다. */
const VERIF_CLASS: Record<VerificationStatus, string> = {
  OFFICIAL: "official", VERIFIED: "verified", PENDING: "pending", UNVERIFIED: "unverified",
};

/** P4/P7: 검증 상태와 최근 확인일은 1급 정보다. */
export function VerificationBadge({ status, lastVerifiedAt }: { status: string; lastVerifiedAt?: string | null }) {
  const v = {
    label: verificationLabel(status),
    cls: VERIF_CLASS[status as VerificationStatus] ?? VERIF_CLASS.UNVERIFIED,
  };
  return (
    <span className={`om-verif om-verif--${v.cls}`}>
      <span aria-hidden="true">●</span>
      {v.label}
      {/*
        * 문자열 앞 10글자를 자르면 **UTC 달력 날짜**가 나온다. 서버는 타임존 없는
        * ISO 를 주고, 그것은 UTC 다 (`parseServerDate`). 한국에서 00~09시 사이에
        * 확인한 것은 그렇게 자르면 하루 전으로 보인다.
        *
        * 같은 화면에서 실제로 갈렸다 — 상세 위쪽 배지는 "· 2026.09.08",
        * 아래쪽 "최근 확인" 은 `formatDay` 라서 "2026.09.09" 였다.
        */}
      {lastVerifiedAt ? <span className="om-verif__when num"> · {formatDay(lastVerifiedAt)}</span> : null}
    </span>
  );
}

export function Skeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="om-skeleton" aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <div className="om-skeleton__row" key={i}>
          <div className="om-skeleton__thumb" />
          <div className="om-skeleton__body">
            <i style={{ width: "62%" }} /><i style={{ width: "40%" }} /><i style={{ width: "52%" }} />
          </div>
        </div>
      ))}
      <span className="sr-only">불러오는 중</span>
    </div>
  );
}

export function EmptyState({ title, body, action, level = 2 }: {
  title: string; body?: string; action?: ReactNode; level?: 2 | 3;
}) {
  const H = (level === 3 ? "h3" : "h2") as "h2" | "h3";
  return (
    <div className="om-empty">
      <H className="om-empty__title">{title}</H>
      {body ? <p className="om-empty__body">{body}</p> : null}
      {action ? <div className="om-empty__action">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ title, body, onRetry, action, level = 2 }: {
  title: string; body?: string; onRetry?: () => void; action?: ReactNode; level?: 2 | 3;
}) {
  const H = (level === 3 ? "h3" : "h2") as "h2" | "h3";
  return (
    <div className="om-error" role="alert">
      <H className="om-error__title">{title}</H>
      {body ? <p className="om-error__body">{body}</p> : null}
      {/* 다시 해도 안 될 일에 "다시 시도" 를 권하지 않는다 — 그럴 땐 갈 곳을 준다. */}
      {action ?? (onRetry ? <Button variant="secondary" size="sm" onClick={onRetry}>다시 시도</Button> : null)}
    </div>
  );
}

/**
 * 출처 링크 하나.
 *
 * 저장된 URL 을 그대로 `href` 에 넣지 않습니다. 출처 URL 규칙이 세 곳에
 * 있었는데 서로 달랐고(수집·후보 스키마는 자격 증명을 막고, 제보 경로는
 * 통과시켰습니다), 서버를 고쳐도 **이미 저장된 줄**이 남습니다. 스키마를
 * 조이면 옛 줄 하나 때문에 화면 전체가 안 열리므로, 그리는 자리에서 봅니다.
 *
 * 링크로 만들 수 없는 주소는 **감추지 않고 글자로 보여 줍니다.** 운영자와
 * 사용자 모두 무엇이 붙어 있는지는 알아야 합니다.
 */
export function SourceLink({ url, label, isOfficial }: {
  url: string;
  label?: string | null;
  isOfficial?: boolean;
}) {
  const href = safeHref(url);
  const text = label ?? hostLabel(url);
  const official = isOfficial ? <b className="om-src__official">공식</b> : null;

  if (!href) {
    return (
      <span className="om-src om-src--unsafe" title={url}>
        <span aria-hidden="true">🔗</span>{text}
        <b className="om-src__official">열 수 없는 주소</b>
        {official}
      </span>
    );
  }
  return (
    <a className="om-src" href={href} target="_blank" rel="noreferrer noopener">
      <span aria-hidden="true">🔗</span>{text}{official}
    </a>
  );
}
